import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/Seo";

export default function NotFound() {
  return (
    <main data-testid="not-found-page" className="flex min-h-[85vh] items-center pt-32 pb-24">
      <Seo
        title="Page Not Found — DISJINX"
        description="The page you're looking for doesn't exist. Head back to DISJINX — AI automation for Australian businesses."
        path="/404"
        noindex
      />
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">
            Error 404 — workflow interrupted
          </p>
          <h1 data-testid="not-found-heading" className="font-display text-8xl font-extrabold leading-none tracking-tight sm:text-9xl lg:text-[14rem]">
            404<span className="text-neutral-300">.</span>
          </h1>

          <div className="mt-12 max-w-xl">
            <svg viewBox="0 0 560 60" className="w-full" aria-hidden="true">
              <line x1="0" y1="30" x2="240" y2="30" stroke="#0a0a0a" strokeWidth="1.5" />
              <line x1="320" y1="30" x2="560" y2="30" stroke="#d4d4d4" strokeWidth="1.5" strokeDasharray="4 8" />
              <motion.rect
                width="10" height="10" x="-5" y="25" fill="#0a0a0a"
                animate={{ x: [-5, 235], opacity: [0, 1, 1, 0] }}
                transition={{ duration: 2.2, repeat: Infinity, times: [0, 0.15, 0.9, 1], ease: "easeInOut" }}
              />
              <motion.rect
                width="12" height="12" x="274" y="24" fill="none" stroke="#0a0a0a" strokeWidth="1.5"
                animate={{ opacity: [0, 1, 0], rotate: [0, 45, 90] }}
                transition={{ duration: 1.4, repeat: Infinity, delay: 1.8, ease: "easeInOut" }}
              />
            </svg>
          </div>

          <p className="mt-12 max-w-md text-base leading-relaxed text-neutral-600">
            This page doesn't exist — the link may be broken or the page may have moved.
            Let's get you back somewhere useful.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/"
              data-testid="not-found-home-link"
              className="group inline-flex items-center gap-2 bg-neutral-950 px-7 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
              Back to home
            </Link>
            <Link
              to="/contact"
              data-testid="not-found-contact-link"
              className="group inline-flex items-center gap-2 border border-neutral-300 px-7 py-4 text-sm font-semibold text-neutral-950 transition-colors duration-200 hover:border-neutral-950"
            >
              Contact us
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
